import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Megaphone, Video, Flame, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  {
    id: "street_team",
    label: "Street Team",
    icon: Megaphone,
    description: "Flyers, stickers, social-media boosts, IRL promo.",
  },
  {
    id: "videographer",
    label: "Videographer",
    icon: Video,
    description: "Shoot live sets, BTS, music videos, vertical content.",
  },
  {
    id: "hype_squad",
    label: "Hype Squad",
    icon: Flame,
    description: "Audience-actor role — control the energy and experience of the crowd at shows.",
  },
];

export default function Crew() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    roles: [],
    availability: "",
    why: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleRole = (id) =>
    setForm((f) => ({
      ...f,
      roles: f.roles.includes(id) ? f.roles.filter((r) => r !== id) : [...f.roles, id],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.location || !form.why) {
      return toast.error("Name, email, city, and 'why' are required.");
    }
    if (form.roles.length === 0) {
      return toast.error("Pick at least one role.");
    }
    setLoading(true);
    try {
      await axios.post(`${API}/volunteer`, {
        ...form,
        phone: form.phone || null,
        availability: form.availability || null,
      });
      toast.success("Application received. We'll reach out within a week.");
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="crew" data-testid="crew-section" className="relative py-24 md:py-32 px-5 md:px-10 max-w-5xl mx-auto">
      <div className="absolute -top-20 right-1/4 w-80 h-80 rounded-full bg-[#FF00E5]/10 blur-[120px] pointer-events-none" aria-hidden="true" />

      <div className="relative text-center mb-12 reveal">
        <span className="font-mono-display text-[11px] uppercase tracking-[0.4em] text-[#00F0FF]">
          07 — Join The Crew
        </span>
        <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl mt-3 leading-[0.95]">
          BUILD THIS <span className="text-gradient-neon">WITH US</span>
        </h2>
        <p className="mt-4 max-w-xl mx-auto text-white/65 text-sm md:text-base">
          Volunteers, hype squad, content creators. Help launch the album — get paid in perks, Discord access, and (eventually) cash.
        </p>
      </div>

      {submitted ? (
        <div className="relative glass-card p-10 md:p-14 text-center reveal" data-testid="crew-success">
          <CheckCircle2 size={40} className="text-[#00F0FF] mx-auto mb-4" />
          <h3 className="font-display font-bold text-2xl md:text-3xl text-cream-soft">Application In.</h3>
          <p className="mt-3 text-white/65 max-w-md mx-auto">
            We read every one. Expect to hear back within 5–7 days. Welcome to the crew.
          </p>
        </div>
      ) : (
        <form data-testid="crew-form" onSubmit={submit} className="relative glass-card p-7 md:p-10 space-y-7 reveal">
          {/* Role checkboxes */}
          <div>
            <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-3">
              Roles · pick one or more
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {ROLES.map((r) => {
                const active = form.roles.includes(r.id);
                return (
                  <button
                    key={r.id}
                    type="button"
                    data-testid={`crew-role-${r.id}`}
                    aria-pressed={active}
                    onClick={() => toggleRole(r.id)}
                    className={`text-left p-4 border-2 transition-all cursor-pointer select-none ${
                      active
                        ? "border-[#FF00E5] bg-[#FF00E5]/10 glow-magenta"
                        : "border-white/15 hover:border-[#FF00E5]/50 bg-black/30 active:bg-[#FF00E5]/5"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span
                        className={`flex items-center justify-center w-9 h-9 border-2 transition-all ${
                          active
                            ? "border-[#FF00E5] bg-[#FF00E5] glow-magenta"
                            : "border-white/40 bg-black/40"
                        }`}
                      >
                        {active && <CheckCircle2 size={20} className="text-black" strokeWidth={3} />}
                      </span>
                      <r.icon size={18} className={active ? "text-[#FF00E5]" : "text-white/55"} />
                    </div>
                    <div className={`font-display font-bold text-base ${active ? "text-cream-soft" : "text-white/80"}`}>
                      {r.label}
                    </div>
                    <p className="mt-1.5 text-xs text-white/55 leading-snug">{r.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
                Name <span className="text-[#FF00E5]">*</span>
              </label>
              <input
                data-testid="crew-input-name"
                type="text"
                required
                value={form.name}
                onChange={set("name")}
                className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#00F0FF] outline-none px-1 py-3 text-cream-soft placeholder:text-white/30 transition-colors"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
                Email <span className="text-[#FF00E5]">*</span>
              </label>
              <input
                data-testid="crew-input-email"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#00F0FF] outline-none px-1 py-3 text-cream-soft placeholder:text-white/30 transition-colors"
                placeholder="you@email.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
                Phone <span className="text-white/30">(optional)</span>
              </label>
              <input
                data-testid="crew-input-phone"
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#FF00E5] outline-none px-1 py-3 text-cream-soft placeholder:text-white/30 transition-colors"
                placeholder="+1 ..."
              />
            </div>
            <div>
              <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
                City <span className="text-[#FF00E5]">*</span>
              </label>
              <input
                data-testid="crew-input-location"
                type="text"
                required
                value={form.location}
                onChange={set("location")}
                className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#00F0FF] outline-none px-1 py-3 text-cream-soft placeholder:text-white/30 transition-colors"
                placeholder="Las Vegas, NV"
              />
            </div>
          </div>

          <div>
            <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
              Availability <span className="text-white/30">(nights / weekends / tour-ready)</span>
            </label>
            <input
              data-testid="crew-input-availability"
              type="text"
              value={form.availability}
              onChange={set("availability")}
              className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#FF00E5] outline-none px-1 py-3 text-cream-soft placeholder:text-white/30 transition-colors"
              placeholder="Weekends + select weeknights"
            />
          </div>

          <div>
            <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
              Why you · what you bring <span className="text-[#FF00E5]">*</span>
            </label>
            <textarea
              data-testid="crew-input-why"
              required
              rows={4}
              value={form.why}
              onChange={set("why")}
              className="w-full bg-black/40 border border-white/10 focus:border-[#FF00E5] outline-none px-4 py-3 text-cream-soft placeholder:text-white/30 transition-colors resize-none"
              placeholder="Past experience, links to your work, what you love about C.A.F."
            />
          </div>

          <button
            type="submit"
            data-testid="crew-submit-button"
            disabled={loading}
            className="btn-shine w-full md:w-auto px-10 py-4 bg-gradient-to-r from-[#FF00E5] via-[#a900b8] to-[#FF00E5] bg-[length:200%_100%] hover:bg-[position:100%_0] text-white font-display font-bold text-xs uppercase tracking-[0.3em] disabled:opacity-60 disabled:cursor-not-allowed glow-magenta transition-all duration-500 flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <><Send size={14} /> Apply For Crew</>}
          </button>
        </form>
      )}
    </section>
  );
}
